import axios from '../store/sagas/axiosConfig';

export const TRAVEFY_SUPPORT_EMAIL = 'support@travelwits.com';

// PostHog feature flag that gates the Travefy export UI.
export const TRAVEFY_FEATURE_FLAG = 'travefy-export';

// Backend `code` values (see the Travefy export contract).
export const TravefyErrorCodes = {
  NOT_ENABLED: 'TRAVEFY_NOT_ENABLED',
  NOT_CONFIGURED: 'TRAVEFY_NOT_CONFIGURED',
  LINK_REQUIRED: 'TRAVEFY_LINK_REQUIRED',
  TRIP_NOT_FOUND: 'TRIP_NOT_FOUND',
  TRIP_EMPTY: 'TRIP_EMPTY',
  EXPORT_IN_PROGRESS: 'EXPORT_IN_PROGRESS',
  EXPORT_FAILED: 'EXPORT_FAILED',
};

/**
 * Turns an axios error into `{ status, code, authorizeUrl, message }`.
 * Never includes request data, so a one-time code cannot leak through it.
 */
export const toTravefyError = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  return {
    status,
    code: data.code,
    authorizeUrl: data.authorizeUrl,
    message: typeof data.error === 'string' ? data.error : undefined,
  };
};

const request = async (config) => {
  try {
    const { data } = await axios(config);
    return data;
  } catch (error) {
    throw toTravefyError(error);
  }
};

export const getTravefyLink = () => request({ method: 'get', url: 'travefy/link' });

export const createTravefyTicket = (returnPath) =>
  request({ method: 'post', url: 'travefy/link/ticket', data: { returnPath } });

export const completeTravefyLink = (n) =>
  request({ method: 'post', url: 'travefy/link/complete', data: { n } });

export const unlinkTravefyAccount = () => request({ method: 'delete', url: 'travefy/link' });

export const exportTripToTravefy = (tripId, { includePrices = false } = {}) =>
  request({
    method: 'post',
    url: `trips/${encodeURIComponent(tripId)}/export/Travefy?includePrices=${includePrices ? 'true' : 'false'}`,
  });

export const isTravefyDisabledError = (error) =>
  error?.status === 403 || error?.code === TravefyErrorCodes.NOT_ENABLED;

export const getExportErrorMessage = (error) => {
  switch (error?.code) {
    case TravefyErrorCodes.TRIP_EMPTY:
      return 'Add something to this trip before sending it to Travefy.';
    case TravefyErrorCodes.TRIP_NOT_FOUND:
      return "We couldn't find this trip. Refresh the page and try again.";
    case TravefyErrorCodes.NOT_CONFIGURED:
      return 'Travefy is not available right now. Try again later.';
    case TravefyErrorCodes.EXPORT_IN_PROGRESS:
      return 'This trip is already being exported to Travefy. Try again in a minute.';
    case TravefyErrorCodes.EXPORT_FAILED:
      return "Travefy didn't accept the trip. Try again in a minute.";
    default:
      return 'Something went wrong. Try again in a minute.';
  }
};

// Same-site relative paths only, never an external URL.
export const isSafeReturnPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');

// Only follow Travefy URLs the backend hands us if they are https.
export const isSafeExternalUrl = (url) => {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};
